import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "store";

import {
  handleUpdateFields,
  CustomWidgetSelectedItem,
  getCustomWidgetItems,
  parseItemConfig,
  getCustomWidgetGames,
  getCustomWidgetCollections,
  getCustomWidgetBonuses,
  getCustomWidgetTournaments,
  getCustomWidgetPaymentMethods,
  getCustomWidgetTags,
  getCustomWidgetProviders,
} from "store/cms/customized/slice";

import RoundBtn from "components/buttons/RoundBtn";

import MainPageDragItem from "../../../../pages/CMS/Widgets/components/MainPageDragItem";
import MainPageSortItem from "../../../../pages/CMS/Widgets/components/MainPageSortItem";

import icon from "assets/icons/burger-icon.svg";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { getFieldErrorKeys } from "helpers/getFieldErrorKeys";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import CompactMultiSelectDropdown from "components/dropdowns/CompactMultiSelectDropdown/CompactMultiSelectDropdown";

type Props = {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  resetErrors: () => void;
  readOnly?: boolean;
};

const CmsCustomizedItemsTab: React.FC<Props> = ({
  readOnly,
  fieldErrors,
  clearFieldError,
  resetErrors,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const payload = useAppSelector((store) => store["cms-customized"].payload);

  const availableItems = useAppSelector(
    (store) => store["cms-customized"].customWidgetItems,
  );

  const isCustomWidgetItemsFetched = useAppSelector(
    (store) => store["cms-customized"].isCustomWidgetItemsFetched,
  );
  const gameOptions = useAppSelector(
    (store) => store["cms-customized"].gamesOptions,
  );

  const collectionOptions = useAppSelector(
    (store) => store["cms-customized"].collectionsOptions,
  );
  const bonusOptions = useAppSelector(
    (store) => store["cms-customized"].bonusesOptions,
  );
  const isBonusesFetched = useAppSelector(
    (store) => store["cms-customized"].isBonusesFetched,
  );
  const tournamentOptions = useAppSelector(
    (store) => store["cms-customized"].tournamentsOptions,
  );
  const isTournamentsFetched = useAppSelector(
    (store) => store["cms-customized"].isTournamentsFetched,
  );
  const paymentMethodOptions = useAppSelector(
    (store) => store["cms-customized"].paymentMethodsOptions,
  );
  const isPaymentMethodsFetched = useAppSelector(
    (store) => store["cms-customized"].isPaymentMethodsFetched,
  );

  const tagOptions = useAppSelector(
    (store) => store["cms-customized"].tagsOptions,
  );

  const isGamesFetched = useAppSelector(
    (store) => store["cms-customized"].isGamesFetched,
  );
  const providerOptions = useAppSelector(
      (store) => store["cms-customized"].providersOptions,
    );

  const [selectedToAdd, setSelectedToAdd] = useState<any[]>([]);
  const [selectedToDelete, setSelectedToDelete] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [bonusSearch, setBonusSearch] = useState("");
  const [tournamentSearch, setTournamentSearch] = useState("");
  const [paymentMethodSearch, setPaymentMethodSearch] = useState("");

  useEffect(() => {
    if (!search.trim()) {
      dispatch(getCustomWidgetGames());
      return;
    }

    const timeout = setTimeout(() => {
      dispatch(getCustomWidgetGames(search));
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [search]);

  useEffect(() => {
    if (!bonusSearch.trim()) {
      dispatch(getCustomWidgetBonuses());
      return;
    }

    const timeout = setTimeout(() => {
      dispatch(
        getCustomWidgetBonuses({
          search: bonusSearch,
        }),
      );
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [bonusSearch, dispatch]);

  useEffect(() => {
    if (!tournamentSearch.trim()) {
      dispatch(getCustomWidgetTournaments());
      return;
    }

    const timeout = setTimeout(() => {
      dispatch(
        getCustomWidgetTournaments({
          search: tournamentSearch,
        }),
      );
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [tournamentSearch, dispatch]);

  useEffect(() => {
    if (!paymentMethodSearch.trim()) {
      dispatch(getCustomWidgetPaymentMethods());
      return;
    }

    const timeout = setTimeout(() => {
      dispatch(
        getCustomWidgetPaymentMethods({
          search: paymentMethodSearch,
        }),
      );
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [paymentMethodSearch, dispatch]);

  useEffect(() => {
    dispatch(getCustomWidgetItems());
    dispatch(getCustomWidgetGames());
    dispatch(getCustomWidgetCollections());
    dispatch(getCustomWidgetTags());
    dispatch(getCustomWidgetProviders());
  }, []);

  const selectedItems = useMemo(() => {
    return payload.items.map((el) => ({
      value: el.itemKey || el.itemType,
      label: t(el.itemType),
      img: icon,
      itemType: el.itemType,
      config: el.config,
      itemKey: el.itemKey,
    }));
  }, [payload.items, t]);

  const freeItems = useMemo(() => {
    return availableItems
      .filter(
        (item) => !payload.items.some((selected) => selected.itemType === item),
      )
      .map((el) => ({
        value: el,
        label: t(el),
        img: icon,
      }));
  }, [availableItems, payload.items, t]);

  const renderCustomContent = (item: any) => {
    const config = parseItemConfig(item.config);
    const index = payload.items.findIndex(
      (el) => (el.itemKey || el.itemType) === item.value,
    );
    const errorsFor = (field: string) => getFieldErrorKeys(
      fieldErrors,
      field,
      `items.${index}.config.${field}`,
    );

    if (item.itemType === "bigWin") {
      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="customized-inline-input">
            <span className="customized-inline-input__label">{t("days")}:</span>
            <InnerInput
              id={`days-${item.value}`}
              hasError={errorsFor("days").length > 0}
              type="number"
              value={String(config.days || "")}
              placeholder={t("days")}
              onChange={(value) => {
                handleUpdateItemConfig(item.value, {
                  ...config,
                  days: Number(value),
                });
                errorsFor("days").forEach(clearFieldError);
              }}
              disabled={readOnly}
            />
          </div>
        </div>
      );
    }

    if (item.itemType === "bigWins" || item.itemType === "lastWins") {
      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="customized-inline-input">
            <span className="customized-inline-input__label">
              {t("limit")}:
            </span>
            <InnerInput
              id={`limit-${item.value}`}
              hasError={errorsFor("limit").length > 0}
              type="number"
              value={String(config.limit || "")}
              placeholder={t("limit")}
              onChange={(value) => {
                handleUpdateItemConfig(item.value, {
                  ...config,
                  limit: Number(value),
                });
                errorsFor("limit").forEach(clearFieldError);
              }}
              disabled={readOnly}
            />
          </div>
        </div>
      );
    }

    if (item.itemType === "collections") {
      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <CompactMultiSelectDropdown
            useFixedMenu
            label={t("collections")}
            items={collectionOptions}
            selectedValues={config.slugs || []}
            onChange={(updatedValues) => {
              handleUpdateItemConfig(item.value, {
                ...config,
                slugs: updatedValues,
              });
            }}
            disabled={readOnly}
          />
        </div>
      );
    }
    if (item.itemType === "bonuses") {
      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <CompactMultiSelectDropdown
            useFixedMenu
            label={t("bonuses")}
            items={bonusOptions}
            selectedValues={config.bonusIds || []}
            onChange={(updatedValues) => {
              handleUpdateItemConfig(item.value, {
                bonusIds: updatedValues,
              });
            }}
            onSearch={setBonusSearch}
            loading={!isBonusesFetched}
            disabled={readOnly}
          />
        </div>
      );
    }
    if (item.itemType === "tournaments") {
      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <CompactMultiSelectDropdown
            useFixedMenu
            label={t("tournaments")}
            items={tournamentOptions}
            selectedValues={config.tournamentIds || []}
            onChange={(updatedValues) => {
              handleUpdateItemConfig(item.value, {
                tournamentIds: updatedValues,
              });
            }}
            onSearch={setTournamentSearch}
            loading={!isTournamentsFetched}
            disabled={readOnly}
          />
        </div>
      );
    }
    if (item.itemType === "paymentMethods") {
      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <CompactMultiSelectDropdown
            useFixedMenu
            label={t("paymentMethods")}
            items={paymentMethodOptions}
            selectedValues={config.paymentMethodIds || []}
            onChange={(updatedValues) => {
              handleUpdateItemConfig(item.value, {
                paymentMethodIds: updatedValues,
              });
            }}
            onSearch={setPaymentMethodSearch}
            loading={!isPaymentMethodsFetched}
            disabled={readOnly}
          />
        </div>
      );
    }
    if (item.itemType === "providers") {
      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <CompactMultiSelectDropdown
            useFixedMenu
            label={t("providers")}
            items={providerOptions}
            selectedValues={config.providerSlugs || []}
            onChange={(updatedValues) => {
              handleUpdateItemConfig(item.value, {
                ...config,
                providerSlugs: updatedValues,
              });
            }}
            disabled={readOnly}
          />
        </div>
      );
    }

    if (item.itemType === "tags") {
      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <CompactMultiSelectDropdown
            useFixedMenu
            label={t("tags")}
            items={tagOptions}
            selectedValues={config.slugs || []}
            onChange={(updatedValues) => {
              handleUpdateItemConfig(item.value, {
                ...config,
                slugs: updatedValues,
              });
            }}
            disabled={readOnly}
          />
        </div>
      );
    }
    if (item.itemType === "gameOfTheWeek") {
      const selectedGameOption = gameOptions.find(
        (game) => String(game.value) === String(config.gameId),
      ) || {
        value: config.gameId,
        label: config.gameTitle,
      };

      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <DefaultDropdown
            useFixedMenu
            placeholder={t("game")}
            options={
              !search.trim() && selectedGameOption?.value
                ? [
                    selectedGameOption,
                    ...gameOptions.filter(
                      (game) =>
                        String(game.value) !== String(selectedGameOption.value),
                    ),
                  ]
                : gameOptions
            }
            value={config.gameId}
            onSelect={(value) => {
              const selectedGame = gameOptions.find(
                (game) => String(game.value) === String(value),
              );
              handleUpdateItemConfig(item.value, {
                ...config,
                gameId: value,
                gameTitle: selectedGame?.label,
              });
            }}
            search={search}
            onSearch={setSearch}
            loading={!isGamesFetched}
            isEllipsis
            disabled={readOnly}
          />
        </div>
      );
    }
    if (item.itemType === "topProvider") {
      const selectedProviderOption = providerOptions.find(
        (provider) => String(provider.value) === String(config.providerSlug),
      ) || {
        value: config.providerSlug,
        label: config.providerSlug,
      };

      return (
        <div
          className="bonus-dropdown-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <DefaultDropdown
            useFixedMenu
            placeholder={t("provider")}
            options={
              selectedProviderOption?.value
                ? [
                    selectedProviderOption,
                    ...providerOptions.filter(
                      (provider) =>
                        String(provider.value) !==
                        String(selectedProviderOption.value),
                    ),
                  ]
                : providerOptions
            }
            value={config.providerSlug}
            onSelect={(value) => {
              handleUpdateItemConfig(item.value, {
                ...config,
                providerSlug: value,
              });
            }}
            isEllipsis
            disabled={readOnly}
          />
        </div>
      );
    }

    return null;
  };

  const handleUpdateItemConfig = (
    itemKey: string,
    updatedConfig: Record<string, any>,
  ) => {
    const updatedItems = payload.items.map((el) => {
      if ((el.itemKey || el.itemType) !== itemKey) {
        return el;
      }

      return {
        ...el,
        config: JSON.stringify(updatedConfig),
      };
    });

    dispatch(
      handleUpdateFields({
        items: updatedItems,
      }),
    );
  };

  const handleAddItems = () => {
    resetErrors();
    const newItems: CustomWidgetSelectedItem[] = [
      ...payload.items,
      ...selectedToAdd.map((el, index) => {
        const item: CustomWidgetSelectedItem = {
          itemType: el.value,
          priorityOrder: payload.items.length + index + 1,
        };

        if (el.value === "bonuses") {
          item.config = JSON.stringify({ bonusIds: [] });
        }

        if (el.value === "tournaments") {
          item.config = JSON.stringify({ tournamentIds: [] });
        }

        if (el.value === "paymentMethods") {
          item.config = JSON.stringify({ paymentMethodIds: [] });
        }

        return item;
      }),
    ];

    dispatch(
      handleUpdateFields({
        items: newItems,
      }),
    );

    setSelectedToAdd([]);
  };

  const handleRemoveItems = () => {
    resetErrors();
    const filtered = payload.items
      .filter(
        (el) =>
          !selectedToDelete.some(
            (selected) => selected.value === (el.itemKey || el.itemType),
          ),
      )
      .map((el, index) => ({
        ...el,
        priorityOrder: index + 1,
      }));

    dispatch(
      handleUpdateFields({
        items: filtered,
      }),
    );

    setSelectedToDelete([]);
  };

  if (!isCustomWidgetItemsFetched) {
    return <ModalTabSkeleton />;
  }

  return (
    <div className="main-page-wrapper customized-page-wrapper">
      <div className="main-page-content">
        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-title">{t("selectedItems")}</div>
            <div className="main-page-widget--list">
              {selectedItems.map((el, index) => (
                <MainPageSortItem
                  isBurger
                  key={el.value}
                  item={el}
                  index={index}
                  moveItem={() => {}}
                  isSelected={selectedToDelete.some(
                    (item) => item.value === el.value,
                  )}
                  handleClick={() => {
                    if (
                      selectedToDelete.some((item) => item.value === el.value)
                    ) {
                      setSelectedToDelete(
                        selectedToDelete.filter(
                          (item) => item.value !== el.value,
                        ),
                      );
                    } else {
                      setSelectedToDelete([...selectedToDelete, el]);
                    }
                  }}
                  customContent={renderCustomContent(el)}
                  readOnly={readOnly}
                />
              ))}
            </div>

            <div className="main-page-btns">
              {!readOnly && (
                <>
                  <RoundBtn
                    txt="+"
                    disabled={!selectedToAdd.length}
                    handleClick={handleAddItems}
                  />

                  <RoundBtn
                    txt="-"
                    disabled={!selectedToDelete.length}
                    handleClick={handleRemoveItems}
                  />
              </>
              )}
            </div>
          </div>
        </div>

        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-title">{t("availableItems")}</div>

            <div className="main-page-list-content">
              {freeItems.map((el) => (
                <MainPageDragItem
                  isBurger
                  key={el.value}
                  item={el}
                  type="custom-widget"
                  isSelected={selectedToAdd.some(
                    (item) => item.value === el.value,
                  )}
                  handleClick={() => {
                    if (selectedToAdd.some((item) => item.value === el.value)) {
                      setSelectedToAdd(
                        selectedToAdd.filter((item) => item.value !== el.value),
                      );
                    } else {
                      setSelectedToAdd([...selectedToAdd, el]);
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmsCustomizedItemsTab;
